// frontend/src/screens/SupportScreen.js
import React, { useState, useContext, useEffect } from 'react';
import { apiSendSupport } from '../services/api';
import { AuthContext } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { logOperationalError } from '../utils/safeLogger';

const MESSAGE_MAX = 2000;

const containerStyle = {
  minHeight: '100vh',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  padding: 16,
  background:
    'radial-gradient(circle at top, rgba(218,165,32,0.2), transparent 60%), radial-gradient(circle at bottom, rgba(75,0,130,0.55), #050013)',
  position: 'relative',
  overflow: 'hidden',
};

const cardStyle = {
  width: '100%',
  maxWidth: 480,
  background:
    'linear-gradient(145deg, rgba(5,0,20,0.55), rgba(18,0,45,0.6))',
  borderRadius: 22,
  border: '1px solid rgba(218,165,32,0.45)',
  boxShadow: '0 20px 40px rgba(0,0,0,0.7)',
  padding: 22,
  color: '#F8F4FF',
  position: 'relative',
  overflow: 'hidden',
  backdropFilter: 'blur(22px)',
};

const glowOrbStyle = {
  position: 'absolute',
  width: 160,
  height: 160,
  borderRadius: '50%',
  background: 'radial-gradient(circle, rgba(218,165,32,0.35), transparent 65%)',
  top: -60,
  right: -40,
  pointerEvents: 'none',
};

const wheelBgStyle = {
  position: 'absolute',
  inset: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  pointerEvents: 'none',
  opacity: 0.45,
  zIndex: 0,
};

const headerPillStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  padding: '4px 10px',
  borderRadius: 999,
  background: 'rgba(75,0,130,0.35)',
  border: '1px solid rgba(218,165,32,0.55)',
  fontSize: 11,
  textTransform: 'uppercase',
  letterSpacing: 1,
  fontWeight: 700,
};

const labelStyle = {
  fontSize: 12,
  marginBottom: 4,
  opacity: 0.95,
};

const inputBaseStyle = {
  width: '100%',
  padding: '9px 11px',
  borderRadius: 12,
  border: '1px solid rgba(148,0,211,0.7)',
  background: 'rgba(5,0,20,0.9)',
  color: '#F8F4FF',
  fontSize: 14,
  outline: 'none',
};

const hintTextStyle = {
  fontSize: 11,
  opacity: 0.7,
  marginTop: 2,
};

export default function SupportScreen() {
  const { t } = useTranslation('common');
  const { user } = useContext(AuthContext);
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [email, setEmail] = useState(user?.email || '');
  useEffect(() => {
    if (user?.email) {
      setEmail((prev) => prev || user.email);
    }
  }, [user?.email]);
  const [sending, setSending] = useState(false);
  const [feedback, setFeedback] = useState('');
  const [feedbackIsError, setFeedbackIsError] = useState(false);
  const navigate = useNavigate();

  async function handleSubmit(e) {
    e.preventDefault();
    setFeedback('');
    setFeedbackIsError(false);

    if (!message.trim()) {
      setFeedback(t('support.validation.messageRequired'));
      setFeedbackIsError(true);
      return;
    }

    if (message.trim().length > MESSAGE_MAX) {
      setFeedback(t('support.validation.messageTooLong', { max: MESSAGE_MAX }));
      setFeedbackIsError(true);
      return;
    }

    try {
      setSending(true);
      const res = await apiSendSupport({
        subject,
        message,
        email,
        userId: user?._id,
        displayName: user?.fullName,
      });

      setFeedback(res.message || t('support.feedback.success'));
      setFeedbackIsError(false);
      setMessage('');
      setSubject('');
    } catch (err) {
      logOperationalError('support.submit', { status: err?.status, code: 'request_failed' });
      setFeedback(
        err?.message ||
          t('support.feedback.error')
      );
      setFeedbackIsError(true);
    } finally {
      setSending(false);
    }
  }

  return (
    <div style={containerStyle}>
      <style>
        {`
          @keyframes spin-slow {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}
      </style>
      <div style={wheelBgStyle}>
        <img
          src="/logo/Zodiacus.jpeg"
          alt=""
          aria-hidden="true"
          style={{
            width: 460,
            height: 460,
            maxWidth: '95vw',
            maxHeight: '95vw',
            borderRadius: '50%',
            objectFit: 'contain',
            backgroundColor: 'rgba(5,0,20,0.9)',
            border: '1px solid rgba(218,165,32,0.85)',
            boxShadow: '0 0 30px rgba(0,0,0,0.9)',
            filter: 'drop-shadow(0 0 26px rgba(218,165,32,0.9))',
            opacity: 0.45,
            animation: 'spin-slow 60s linear infinite',
          }}
        />
      </div>
      <div style={cardStyle}>
        <div style={glowOrbStyle} />

        <div
          style={{
            position: 'relative',
            zIndex: 1,
            display: 'flex',
            justifyContent: 'center',
            marginBottom: 10,
          }}
        >
          <div
            style={{
              width: 130,
              height: 130,
              borderRadius: '50%',
              overflow: 'hidden',
              boxShadow: '0 0 22px rgba(218,165,32,0.7)',
              border: '1px solid rgba(218,165,32,0.85)',
              backgroundColor: 'rgba(5,0,20,0.95)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <img
              src="/logo/Zodiacus.jpeg"
              alt="Zodiacus"
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                animation: 'spin-slow 60s linear infinite',
              }}
            />
          </div>
        </div>

        <div style={{ marginBottom: 16, position: 'relative', zIndex: 1 }}>
          <div style={headerPillStyle}>
            <span aria-hidden="true">💬</span>
            <span>{t('support.badge')}</span>
          </div>

          <h1
            style={{
              margin: '10px 0 4px',
              fontSize: 22,
              letterSpacing: 0.3,
            }}
          >
            {t('support.title')}
          </h1>

          <p style={{ fontSize: 13, opacity: 0.9, lineHeight: 1.5 }}>
            {t('support.description')}
          </p>

          {user && (
            <div
              style={{
                marginTop: 8,
                fontSize: 11,
                opacity: 0.8,
              }}
            >
              {t('support.connectedAs')}{' '}
              <strong>{user.fullName || user.displayName || user.email}</strong>
            </div>
          )}
        </div>

        <form
          onSubmit={handleSubmit}
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 10,
            position: 'relative',
            zIndex: 1,
          }}
        >
          <div>
            <div style={labelStyle}>{t('support.fields.subject.label')}</div>
            <input
              id="support-subject"
              name="subject"
              type="text"
              autoComplete="off"
              placeholder={t('support.fields.subject.placeholder')}
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              style={inputBaseStyle}
            />
          </div>

          <div>
            <div style={labelStyle}>{t('support.fields.message.label')}</div>
            <textarea
              id="support-message"
              name="message"
              autoComplete="off"
              placeholder={t('support.fields.message.placeholder')}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={6}
              maxLength={MESSAGE_MAX}
              style={{
                ...inputBaseStyle,
                resize: 'vertical',
                lineHeight: 1.45,
              }}
            />
            <div style={{ ...hintTextStyle, display: 'flex', justifyContent: 'space-between', gap: 10 }}>
              <span>
                {t('support.fields.message.hint')}
              </span>
              <span style={{ whiteSpace: 'nowrap' }}>
                {message.length}/{MESSAGE_MAX}
              </span>
            </div>
          </div>

          <div>
            <div style={labelStyle}>{t('support.fields.email.label')}</div>
            <input
              id="support-email"
              name="email"
              type="email"
              autoComplete="email"
              placeholder={t('support.fields.email.placeholder')}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              style={inputBaseStyle}
            />
          </div>

          <button
            type="submit"
            disabled={sending}
            style={{
              marginTop: 6,
              padding: '10px 14px',
              borderRadius: 999,
              border: 'none',
              fontWeight: 800,
              fontSize: 15,
              cursor: sending ? 'default' : 'pointer',
              background: 'linear-gradient(135deg, #DAA520, #FFD700)',
              color: '#fff',
              opacity: sending ? 0.7 : 1,
              boxShadow: '0 0 18px rgba(218,165,32,0.55)',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 6,
            }}
          >
            {sending ? (
              <>
                <span aria-hidden="true">⏳</span>
                {t('support.actions.sending')}
              </>
            ) : (
              <>
                <span aria-hidden="true">🚀</span>
                {t('support.actions.send')}
              </>
            )}
          </button>
        </form>

        {feedback && (
          <div
            style={{
              marginTop: 10,
              fontSize: 13,
              opacity: 0.95,
              padding: '8px 10px',
              borderRadius: 10,
              background: feedbackIsError
                ? 'rgba(220,53,69,0.15)'
                : 'rgba(40,167,69,0.18)',
              border: feedbackIsError
                ? '1px solid rgba(220,53,69,0.6)'
                : '1px solid rgba(40,167,69,0.6)',
            }}
          >
            {feedback}
          </div>
        )}

        <button
          type="button"
          onClick={() => navigate(-1)}
          style={{
            marginTop: 18,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            padding: '7px 16px',
            borderRadius: 999,
            border: '1px solid rgba(226,210,255,0.9)',
            background: 'linear-gradient(135deg, rgba(10,0,35,0.92), rgba(32,0,70,0.96))',
            color: '#E9E2FF',
            fontSize: 13,
            cursor: 'pointer',
            textDecoration: 'none',
            boxShadow: '0 6px 14px rgba(0,0,0,0.65)',
            position: 'relative',
            zIndex: 1,
            letterSpacing: 0.3,
            fontWeight: 600,
          }}
        >
          <span aria-hidden="true">⬅</span>
          <span>{t('actions.back')}</span>
        </button>
      </div>
    </div>
  );
}
